import { Icon } from '@toggle/design-system';
import { formatTicker, TrackingStatus } from '@toggle/toggle';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { AssetLogoContainer } from '~/components/asset-logo-container/AssetLogoContainer';
import { useGetInsightWidgetData } from '~/services/insights/insights-service';
import { getCardDate } from '~/utils/card-utils/card-utils';

import * as SharedStyles from '../../AlertsCard.styles';
import * as S from './AlertsInsightCard.styles';

type AlertsInsightCardAlertEvent = {
  created_at?: string;
  source_id: string;
  title?: string;
  read?: boolean;
  tracking_status?: TrackingStatus;
  metadata?: {
    days_remaining?: number;
  };
  relation?: string;
};
interface AlertsInsightCardProps {
  alertEvent: AlertsInsightCardAlertEvent;
  setIsDropdownOpen?: (isDropdownOpen: boolean) => void;
  variant?: 'toast' | 'card';
  dropdown?: React.ReactNode;
}

export const AlertsInsightCard = ({
  alertEvent,
  dropdown,
  variant = 'card',
}: AlertsInsightCardProps) => {
  const { t } = useTranslation('alerts');
  const {
    created_at: createdAt,
    source_id: sourceId,
    title,
    read = false,
    tracking_status: trackingStatus,
    metadata,
    relation,
  } = alertEvent;
  const isToastVariant = variant === 'toast';

  const { data, isLoading } = useGetInsightWidgetData(sourceId);
  const entity = data?.data?.entity;

  const isTrackingActive = trackingStatus === 'active';
  const isTrackingCompleted = trackingStatus === 'completed';

  if (isLoading) {
    return <SharedStyles.StyledLoader />;
  }

  if (!entity) {
    return null;
  }

  return (
    <>
      <SharedStyles.Header>
        <SharedStyles.HeaderLeft>
          <S.AssetNameText>{entity.name}</S.AssetNameText>
          <SharedStyles.Dot />
          <S.SecondaryAssetInfoText>
            {formatTicker(entity)}
          </S.SecondaryAssetInfoText>
          {relation && (
            <>
              <SharedStyles.Dot />
              <S.SecondaryAssetInfoText>{relation}</S.SecondaryAssetInfoText>
            </>
          )}
        </SharedStyles.HeaderLeft>
        {!isToastVariant && createdAt && (
          <SharedStyles.HeaderRight>
            <SharedStyles.DateSpan>
              {getCardDate(new Date(createdAt))}
            </SharedStyles.DateSpan>
            {dropdown}
          </SharedStyles.HeaderRight>
        )}
      </SharedStyles.Header>
      <SharedStyles.BodyWrapper>
        <SharedStyles.LogoWrapper>
          <AssetLogoContainer
            entity={entity}
            size={32}
            shouldUseBorderOnFallback
          />
          <S.LogoBadge>
            <Icon
              iconName="ExploreNav"
              size={12}
              fillColor="var(--icon-decorative-indigo)"
            />
          </S.LogoBadge>
        </SharedStyles.LogoWrapper>
        <SharedStyles.TextContent>
          <SharedStyles.PrimaryText
            $read={read}
            $isToastVariant={isToastVariant}
          >
            {title}
          </SharedStyles.PrimaryText>
          {!isToastVariant && (trackingStatus || metadata?.days_remaining) && (
            <S.SecondaryText>
              {trackingStatus && (
                <S.TrackingStatus $isTracking={isTrackingActive}>
                  •{' '}
                  {t(
                    `alerts:alfredInsightCard.trackingStatus.${trackingStatus}`
                  )}
                </S.TrackingStatus>
              )}
              {metadata?.days_remaining !== undefined && (
                <S.Horizon>
                  ·{' '}
                  {t(
                    `alerts:alfredInsightCard.${
                      isTrackingCompleted ? 'daysElapsed' : 'daysRemaining'
                    }`,
                    {
                      count: metadata.days_remaining,
                    }
                  )}
                </S.Horizon>
              )}
            </S.SecondaryText>
          )}
        </SharedStyles.TextContent>
      </SharedStyles.BodyWrapper>
    </>
  );
};
